import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment';
import translate from '../../commonComponents/translate';
import Title from '../../commonComponents/Title';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../commonComponents/pageLayout/SubHeader';
import BravonCalendar from '../../commonComponents/agenda/BravonAgenda';
import Notifications from '../../services/notifications';
import UpcomingEvents from './UpcomingEvents';
import Loader from '../../services/loader';
import CalendlyPopover from '../../commonComponents/agenda/popover/CalendlyPopover';
import AgendaEvent from '../../commonComponents/agenda/components/AgendaEvent';

// region Request Calendly events
/**
 * {GET} /user/agenda/calendly/events/list
 * @param startDate {number} - To request from a specific date forward
 * @param endDate {number} - To request until a specific date
 * @param index {number} - To request a limited amount of events
 * @returns {Promise<*[]>}
 */
const getAgendaCalendlyEvents = async (
  startDate = undefined,
  endDate = undefined,
  index = undefined,
) => {
  try {
    const params = {
      startDate,
      endDate,
      index,
    };
    const { data: result } = await axios.get(
      '/api/user/agenda/calendly/events/list',
      {
        params,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Organization',
        },
      },
    );
    return result.data || [];
  } catch (err) {
    console.error(err);
    return [];
  }
};
// endregion Request Calendly events

// region Modified Events
/**
 * Modify events so we can display data in our way
 * @param events
 * @returns {*}
 */
const modifiedEvents = events =>
  events.map((event, index) => ({
    title: <AgendaEvent event={event} />,
    name: event?.name,
    start: new Date(event.startDate),
    end: new Date(event.endDate),
    id: index,
    status: event.status,
  }));
// endregion Modified events

function MyAgenda(props) {
  const { t } = props;

  // region Loader
  const loader = useRef(new Loader('#my-agenda'));
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);
  // endregion Loader

  const [events, setEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  // region initialize events & upcoming events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const startOfLastWeekPreviousMonth = moment()
          .utc()
          .subtract(1, 'month')
          .endOf('month')
          .startOf('week')
          .valueOf();

        // End of first week of next month
        const endOfFirstWeekNextMonth = moment()
          .utc()
          .add(1, 'month')
          .startOf('month')
          .endOf('week')
          .valueOf();

        const [agendaEvents, upcomingAgendaEvents] = await Promise.all([
          getAgendaCalendlyEvents(
            startOfLastWeekPreviousMonth,
            endOfFirstWeekNextMonth,
          ),
          getAgendaCalendlyEvents(moment().utc().valueOf(), undefined, 8),
        ]);

        setEvents(modifiedEvents(agendaEvents));
        setUpcomingEvents(modifiedEvents(upcomingAgendaEvents));
      } catch (err) {
        Notifications.showErrorFromRequest(err);
      }
    };

    fetchEvents();
  }, []);
  // endregion initialize events & upcoming events

  // region handle agenda change
  /**
   * Manages the request to API with the new dates
   * @type {(function(*, *): Promise<void>)|*}
   */
  const handleDateChange = useCallback(
    async (startDate, endDate) => {
      setIsLoading(true);
      const agendaEvents = await getAgendaCalendlyEvents(startDate, endDate);
      setIsLoading(false);
      setEvents(modifiedEvents(agendaEvents));
    },
    [setEvents, setIsLoading],
  );

  /**
   * Manages the interaction on agenda (Next & Back)
   * @type {(function(*): void)|*}
   */
  const handleRangeChange = useCallback(
    range => {
      // if start and end attributes are defined, agenda is in month view
      if (range.start && range.end) {
        handleDateChange(
          moment(range.start).utc().valueOf(),
          moment(range.end).utc().valueOf(),
        );
      } else if (Array.isArray(range)) {
        // it is an array with 1 element, agenda is in daily view
        if (range.length === 1) {
          const startOfDay = moment(range[0]).startOf('day').valueOf(); // Local start of the day
          const endOfDay = moment(range[0]).endOf('day').valueOf(); // Local end of the da
          // if only 1 date, we need to calculate the end of the day (23:59:59)h
          handleDateChange(startOfDay, endOfDay);
        } else {
          // it is an array with more elements, then send range
          handleDateChange(
            moment(range[0]).utc().valueOf(),
            moment(range[range.length - 1])
              .utc()
              .valueOf(),
          );
        }
      }
    },
    [handleDateChange],
  );
  // region handle agenda change

  return (
    <Title title={t('my.agenda')}>
      <ContentContainer>
        <SubHeader title={t('my.agenda')} />
        <div id="my-agenda" className="my-agenda-container">
          <div className="m-content m-portlet my-agenda-calendar">
            <BravonCalendar
              onRangeChange={handleRangeChange}
              events={events}
              popoverContent={CalendlyPopover}
              popoverClass="my-agenda-popover-calendly"
            />
          </div>
          <UpcomingEvents events={upcomingEvents} />
        </div>
      </ContentContainer>
    </Title>
  );
}

MyAgenda.propTypes = {
  t: PropTypes.func.isRequired,
};

export default translate(MyAgenda);
